/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import styles from './styles.module.scss';
import cx from 'classnames';
import azCommonStyles from '@/theme/globals.module.scss';
import ClearanceBadgeWrapper from '@/components/AZCustomComponent/ClearanceBadge';
import StrikeThroughPriceWrapper from '@/components/AZCustomComponent/StrikethroughPricing';
import { useLocale } from '@/hooks/useLocale';
import { Grid } from '@/components/Grid';
import { countryCodes } from '@/constants/locale';
import { useFeatureFlag } from '@/features/globalConfig';
import type { SkuPricingAndAvailabilityFromAPI } from '@/types/availability';
import { useForteDecisionFlagWithTrueDefault } from '@/features/forte/api/getForteDecision';

type PdpPriceProps = {
  dealAvailable: boolean;
  dealDrawerPDPEnabled: boolean;
  priceLabel: string;
  isClearanceItem: boolean;
  hasStrikethroughPrice: boolean;
  count: number;
  corePriceInfoPopover: React.ReactNode;
  priceInfoPopover: React.ReactNode;
  saveNowOrRebateBadges: React.ReactNode;
  skuPricingAndAvailability: SkuPricingAndAvailabilityFromAPI | undefined;
  discountedPrice: string | null;
  updatedDiscountedPrice: number;
  retailPriceInt: number;
  noGridSpacing?: boolean;
  unsetMargin?: boolean;
};

const PdpPrice = ({
  dealAvailable,
  dealDrawerPDPEnabled,
  priceLabel,
  isClearanceItem,
  hasStrikethroughPrice,
  corePriceInfoPopover,
  priceInfoPopover,
  saveNowOrRebateBadges,
  skuPricingAndAvailability,
  count,
  discountedPrice,
  updatedDiscountedPrice,
  retailPriceInt,
  noGridSpacing,
  unsetMargin,
}: PdpPriceProps) => {
  const locale = useLocale();
  const strikeThroughPricingEnabled = useFeatureFlag('SHOW_STRIKE_THROUGH_PRICING') === 'true';
  const totalCorePrice = (skuPricingAndAvailability?.corePrice ?? 0) * count;
  const isProductDetailsPageV2Enabled = useForteDecisionFlagWithTrueDefault('pdpRedesignEnabled');

  const showCartDiscountCaption = () => {
    if (locale === countryCodes.mx) {
      return dealAvailable && !strikeThroughPricingEnabled;
    } else {
      return dealAvailable;
    }
  };

  const grid = (children: React.ReactNode) => {
    return noGridSpacing ? (
      <Grid container>{children}</Grid>
    ) : (
      <Grid container spacing={1}>
        {children}
      </Grid>
    );
  };

  return (
    <div className={styles.pdpPriceInfo}>
      {totalCorePrice ? (
        <>
          {dealAvailable && (
            <div
              className={cx(azCommonStyles['az-caption'], styles.discountCaption)}
              id="cartDiscountCaption"
            >
              {!dealDrawerPDPEnabled && priceLabel}
            </div>
          )}
          <div className={cx(styles.coreInfoWrapper, { [styles.unsetMargin]: unsetMargin })}>
            {strikeThroughPricingEnabled && discountedPrice && (
              <StrikeThroughPriceWrapper
                alignRight
                price={updatedDiscountedPrice}
                wasPrice={retailPriceInt}
              />
            )}
            {corePriceInfoPopover}
            {isClearanceItem && (
              <div
                className={cx(
                  styles.coreClearanceWrapper,
                  hasStrikethroughPrice ? '' : styles.marginBtm,
                )}
              >
                <ClearanceBadgeWrapper />
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          {grid(
            <>
              {showCartDiscountCaption() && (
                <Grid xs={12}>
                  <div className={cx(azCommonStyles['az-caption'])} id="cartDiscountCaption">
                    {!dealDrawerPDPEnabled && priceLabel}
                  </div>
                </Grid>
              )}
              <Grid
                item
                xs={12}
                className={cx({
                  [styles.pdpRedesign]: isProductDetailsPageV2Enabled,
                })}
              >
                {priceInfoPopover}
              </Grid>
              <Grid
                className={cx(styles.clearanceWrapper, {
                  [styles.pdpRedesign]: isProductDetailsPageV2Enabled,
                })}
              >
                {isClearanceItem && <ClearanceBadgeWrapper />}
              </Grid>
            </>,
          )}
          {saveNowOrRebateBadges !== null && isClearanceItem && !isProductDetailsPageV2Enabled && (
            <div className={styles.horizantalStrikeLine} />
          )}
        </>
      )}
      {saveNowOrRebateBadges}
    </div>
  );
};

export default PdpPrice;
